import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography, Button, Box, Alert, CircularProgress, TextField,
  Autocomplete, Divider, ImageList, ImageListItem,
  IconButton, Skeleton, LinearProgress, Checkbox, FormControlLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import ModalWrapper from '../components/ModalWrapper';
import StoreForm from '../forms/StoreForm';
import {
  Store, StoreFormData, emptyStoreFormData,
  PlaceSearchResult, PlaceDetails, StoreImage
} from '../types/Store';
import { apiFetch } from '../apiFetch';

// localStorage key for remembering location
const LOCATION_STORAGE_KEY = 'storeFormLocation';

interface SavedLocation {
  type: 'auto' | 'manual';
  manualLocation?: string;
  lat?: number;
  lng?: number;
}

interface StoreFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (store: Store) => void;
  store?: Store | null;
  existingStores?: Store[];
}

interface ThumbnailImage {
  index: number;
  thumbnail_url: string;
  attribution: string | null;
  loading?: boolean;
}

const StoreFormModal: React.FC<StoreFormModalProps> = ({ open, onClose, onSuccess, store, existingStores = [] }) => {
  const [formData, setFormData] = useState<StoreFormData>(emptyStoreFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof StoreFormData, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Google Places search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PlaceSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // User location for search bias
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [manualLocation, setManualLocation] = useState('');
  const [useMyLocation, setUseMyLocation] = useState(true);
  const [showLocationInput, setShowLocationInput] = useState(false);
  const [hasRememberedLocation, setHasRememberedLocation] = useState(false);

  // Ref for location input focus
  const locationInputRef = useRef<HTMLInputElement>(null);
  // Ref for search input focus
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Images state
  const [thumbnailImages, setThumbnailImages] = useState<ThumbnailImage[]>([]);
  const [storeImages, setStoreImages] = useState<StoreImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesProgress, setImagesProgress] = useState({ current: 0, total: 0, phase: '' });
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  // Custom upload state
  const [uploadingCustom, setUploadingCustom] = useState(false);
  const [customImagePreview, setCustomImagePreview] = useState<string | null>(null);
  const [useCustomImage, setUseCustomImage] = useState(false);
  const [currentStoreImage, setCurrentStoreImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  // Opening hours from Google
  const [openingHoursPeriods, setOpeningHoursPeriods] = useState<Array<{
    day: number;
    open_time: string;
    close_time: string | null;
  }>>([]);

  const isEditing = !!store;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Determine if we have a valid location for search
  const hasAutoLocation = userLocation !== null;
  const hasManualLocation = manualLocation.trim().length >= 3;
  const hasValidLocation = (useMyLocation && hasAutoLocation) || (!useMyLocation && hasManualLocation);
  const canSearch = hasValidLocation && searchQuery.trim().length > 0;

  // Get the display text for current location
  const getLocationDisplayText = () => {
    if (useMyLocation && hasAutoLocation) {
      return 'My Location';
    } else if (!useMyLocation && hasManualLocation) {
      return manualLocation.trim();
    }
    return '';
  };

  // Get the search field placeholder/label
  const getSearchPlaceholder = () => {
    if (hasValidLocation) {
      return `Search for stores around ${getLocationDisplayText()}`;
    }
    return 'Enter a location to search for stores';
  };

  // Check if a place is already in user's stores
  const isExistingStore = (placeId: string): boolean => {
    return existingStores.some(s => s.google_place_id === placeId && !s.deleted);
  };

  // Save location to localStorage
  const saveLocationToStorage = (location: SavedLocation) => {
    try {
      localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify(location));
    } catch (err) {
      console.error('Failed to save location to localStorage:', err);
    }
  };

  // Load location from localStorage
  const loadLocationFromStorage = (): SavedLocation | null => {
    try {
      const saved = localStorage.getItem(LOCATION_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (err) {
      console.error('Failed to load location from localStorage:', err);
    }
    return null;
  };

  // Memoized function for geolocation
  const tryGeolocation = useCallback(() => {
    if (navigator.geolocation) {
      setLocationLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(coords);
          setLocationLoading(false);
          setUseMyLocation(true);
          setShowLocationInput(false);
          // Save auto location
          saveLocationToStorage({ type: 'auto', lat: coords.lat, lng: coords.lng });
          setHasRememberedLocation(true);
        },
        () => {
          setLocationLoading(false);
          setUseMyLocation(false);
          setShowLocationInput(true);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    } else {
      setLocationLoading(false);
      setUseMyLocation(false);
      setShowLocationInput(true);
    }
  }, []);

  // Get user location for search bias
  useEffect(() => {
    if (open && !isEditing) {
      // First, check for saved location
      const savedLocation = loadLocationFromStorage();

      if (savedLocation) {
        setHasRememberedLocation(true);

        if (savedLocation.type === 'auto' && savedLocation.lat && savedLocation.lng) {
          // Saved auto location - use it directly
          setUserLocation({ lat: savedLocation.lat, lng: savedLocation.lng });
          setUseMyLocation(true);
          setShowLocationInput(false);
          setLocationLoading(false);
        } else if (savedLocation.type === 'manual' && savedLocation.manualLocation) {
          // Saved manual location
          setManualLocation(savedLocation.manualLocation);
          setUseMyLocation(false);
          setShowLocationInput(false);
          setLocationLoading(false);
        } else {
          // Invalid saved data, fall back to geolocation
          setHasRememberedLocation(false);
          tryGeolocation();
        }
      } else {
        // No saved location, try geolocation
        setHasRememberedLocation(false);
        tryGeolocation();
      }
    } else if (open && !isEditing) {
      setLocationLoading(false);
      setUseMyLocation(false);
      setShowLocationInput(true);
    }
  }, [open, isEditing, tryGeolocation]);

  // Focus location input when it becomes visible
  useEffect(() => {
    if (showLocationInput && locationInputRef.current) {
      // Small delay to ensure the field is rendered
      setTimeout(() => {
        locationInputRef.current?.focus();
      }, 100);
    }
  }, [showLocationInput]);

  // Focus search input when we have a remembered location and modal opens
  useEffect(() => {
    if (open && !isEditing && hasRememberedLocation && !locationLoading && hasValidLocation && !showLocationInput) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 150);
    }
  }, [open, isEditing, hasRememberedLocation, locationLoading, hasValidLocation, showLocationInput]);

  // Populate form when editing
  useEffect(() => {
    if (store) {
      setFormData({
        name: store.name || '',
        address_line1: store.address_line1 || '',
        address_line2: store.address_line2 || '',
        city: store.city || '',
        state: store.state || '',
        postal_code: store.postal_code || '',
        country: store.country || '',
        phone_number: store.phone_number || '',
        website: store.website || '',
        latitude: store.latitude?.toString() || '',
        longitude: store.longitude?.toString() || '',
        google_place_id: store.google_place_id || '',
        image_url: store.image_url || '',
        thumbnail_url: store.thumbnail_url || '',
        icon_url: store.icon_url || '',
        image_attribution: store.image_attribution || '',
      });
      if (store.image_url) {
        setCurrentStoreImage(store.thumbnail_url || store.image_url);
        setUseCustomImage(true);
      } else {
        setCurrentStoreImage(null);
        setUseCustomImage(false);
      }
    } else {
      setFormData(emptyStoreFormData);
      setThumbnailImages([]);
      setStoreImages([]);
      setSelectedImageIndex(null);
      setOpeningHoursPeriods([]);
      setHasSearched(false);
      setDropdownOpen(false);
      setCustomImagePreview(null);
      setUseCustomImage(false);
      setCurrentStoreImage(null);
    }
    setErrors({});
    setApiError(null);
    setSearchQuery('');
    setSearchResults([]);
  }, [store, open]);

  // Update form data when full images finish loading and user has selected an image
  useEffect(() => {
    if (selectedImageIndex !== null && storeImages.length > 0 && !useCustomImage) {
      const image = storeImages[selectedImageIndex];
      if (image) {
        setFormData((prev) => ({
          ...prev,
          image_url: image.image_url,
          thumbnail_url: image.thumbnail_url,
          icon_url: image.icon_url,
          image_attribution: image.attribution || '',
        }));
      }
    }
  }, [storeImages, selectedImageIndex, useCustomImage]);

  const handleChange = (field: keyof StoreFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // Handle location input blur - hide input if valid and save to storage
  const handleLocationBlur = () => {
    if (hasManualLocation) {
      setShowLocationInput(false);
      // Save manual location
      saveLocationToStorage({ type: 'manual', manualLocation: manualLocation.trim() });
      setHasRememberedLocation(true);
    } else if (useMyLocation && hasAutoLocation) {
      setShowLocationInput(false);
    }
  };

  // Handle "Use my location" checkbox change
  const handleUseMyLocationChange = (checked: boolean) => {
    setUseMyLocation(checked);
    if (checked) {
      setShowLocationInput(false);
      // Save auto location
      if (userLocation) {
        saveLocationToStorage({ type: 'auto', lat: userLocation.lat, lng: userLocation.lng });
        setHasRememberedLocation(true);
      }
    }
  };

  // Handle Change Location button click
  const handleChangeLocationClick = () => {
    setShowLocationInput(true);
  };

  // Search Google Places
  const handleSearch = async () => {
    if (!searchQuery.trim() || !hasValidLocation) return;

    setSearchLoading(true);
    setHasSearched(true);
    try {
      let url = `${apiUrl}/api/places/search/?query=${encodeURIComponent(searchQuery)}`;
      if (useMyLocation && userLocation) {
        url += `&lat=${userLocation.lat}&lng=${userLocation.lng}`;
      } else if (!useMyLocation && manualLocation.trim()) {
        url += `&location=${encodeURIComponent(manualLocation.trim())}`;
      }
      const data = await apiFetch(url);
      setSearchResults(data.results || []);
      setDropdownOpen(true);
    } catch (err: any) {
      setApiError(err.message || 'Failed to search places.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle Enter key in search
  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canSearch) {
        handleSearch();
      }
    }
  };

  // Handle place selection
  const handlePlaceSelect = async (place: PlaceSearchResult | null) => {
    if (!place) {
      return;
    }

    setSearchLoading(true);
    setThumbnailImages([]);
    setStoreImages([]);
    setSelectedImageIndex(null);
    setDropdownOpen(false);
    setCustomImagePreview(null);
    setUseCustomImage(false);
    setCurrentStoreImage(null);

    try {
      const details: PlaceDetails = await apiFetch(`${apiUrl}/api/places/details/?place_id=${place.place_id}`);

      const truncateCoordinate = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '';
        return value.toFixed(6);
      };

      setFormData({
        name: details.name || '',
        address_line1: details.address_line1 || '',
        address_line2: '',
        city: details.city || '',
        state: details.state || '',
        postal_code: details.postal_code || '',
        country: details.country || '',
        phone_number: details.phone_number || '',
        website: details.website || '',
        latitude: truncateCoordinate(details.latitude),
        longitude: truncateCoordinate(details.longitude),
        google_place_id: place.place_id,
        image_url: '',
        thumbnail_url: '',
        icon_url: '',
        image_attribution: '',
      });

      setOpeningHoursPeriods(details.opening_hours_periods || []);

      if (details.photo_references && details.photo_references.length > 0) {
        await fetchImages(place.place_id, details.photo_references);
      }
    } catch (err: any) {
      setApiError(err.message || 'Failed to fetch place details.');
    } finally {
      setSearchLoading(false);
    }
  };

  // Fetch images with progress
  const fetchImages = async (placeId: string, refs: Array<{ reference: string; attributions: string[] }>) => {
    setImagesLoading(true);
    setImagesProgress({ current: 0, total: refs.length, phase: 'Checking for cached images...' });

    try {
      const cachedCheck = await apiFetch(`${apiUrl}/api/places/images/?place_id=${placeId}`);

      if (cachedCheck.cached && cachedCheck.images.length > 0) {
        setStoreImages(cachedCheck.images);
        setImagesLoading(false);
        return;
      }

      const placeholders: ThumbnailImage[] = refs.slice(0, 5).map((_, idx) => ({
        index: idx + 1,
        thumbnail_url: '',
        attribution: null,
        loading: true,
      }));
      setThumbnailImages(placeholders);

      setImagesProgress({ current: 0, total: refs.length, phase: 'Loading thumbnails...' });

      const thumbResponse = await apiFetch(`${apiUrl}/api/places/images/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          place_id: placeId,
          photo_references: refs,
          phase: 'thumbnails',
        }),
      });

      if (thumbResponse.images) {
        const thumbs: ThumbnailImage[] = thumbResponse.images.map((img: any) => ({
          index: img.index,
          thumbnail_url: img.thumbnail_url,
          attribution: img.attribution,
          loading: false,
        }));
        setThumbnailImages(thumbs);
      }

      setImagesProgress({ current: 0, total: refs.length, phase: 'Processing full images...' });

      const fullResponse = await apiFetch(`${apiUrl}/api/places/images/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          place_id: placeId,
          photo_references: refs,
          phase: 'full',
        }),
      });

      if (fullResponse.images) {
        setStoreImages(fullResponse.images);
      }

    } catch (err: any) {
      console.error('Failed to fetch images:', err);
    } finally {
      setImagesLoading(false);
    }
  };

  // Handle image selection from Google images
  const handleImageSelect = (index: number) => {
    setUseCustomImage(false);
    setCustomImagePreview(null);
    setCurrentStoreImage(null);

    if (selectedImageIndex === index) {
      setSelectedImageIndex(null);
      setFormData((prev) => ({
        ...prev,
        image_url: '',
        thumbnail_url: '',
        icon_url: '',
        image_attribution: '',
      }));
    } else {
      setSelectedImageIndex(index);

      const attribution = storeImages.length > 0
        ? storeImages[index]?.attribution
        : thumbnailImages[index]?.attribution;

      if (storeImages.length > 0) {
        const image = storeImages[index];
        setFormData((prev) => ({
          ...prev,
          image_url: image.image_url,
          thumbnail_url: image.thumbnail_url,
          icon_url: image.icon_url,
          image_attribution: attribution || '',
        }));
      } else {
        const thumb = thumbnailImages[index];
        setFormData((prev) => ({
          ...prev,
          image_url: '',
          thumbnail_url: thumb.thumbnail_url,
          icon_url: '',
          image_attribution: attribution || '',
        }));
      }
    }
  };

  // Handle custom image upload
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setApiError('Please select an image file.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setApiError('Image file is too large. Maximum size is 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setCustomImagePreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    setUploadingCustom(true);
    setApiError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const makeUploadRequest = async (token: string) => {
        return fetch(`${apiUrl}/api/places/upload-image/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formDataUpload,
        });
      };

      let response = await makeUploadRequest(localStorage.getItem('access_token') || '');

      if (response.status === 401) {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const refreshResponse = await fetch(`${apiUrl}/api/token/refresh/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh: refreshToken }),
          });

          if (refreshResponse.ok) {
            const tokens = await refreshResponse.json();
            localStorage.setItem('access_token', tokens.access);
            response = await makeUploadRequest(tokens.access);
          } else {
            throw new Error('Session expired. Please log in again.');
          }
        } else {
          throw new Error('Session expired. Please log in again.');
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const imageData = await response.json();

      setFormData((prev) => ({
        ...prev,
        image_url: imageData.image_url,
        thumbnail_url: imageData.thumbnail_url,
        icon_url: imageData.icon_url,
        image_attribution: 'User uploaded',
      }));

      setUseCustomImage(true);
      setSelectedImageIndex(null);
      setCurrentStoreImage(null);

    } catch (err: any) {
      setApiError(err.message || 'Failed to upload image.');
      setCustomImagePreview(null);
    } finally {
      setUploadingCustom(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  // Clear custom image
  const handleClearCustomImage = () => {
    setCustomImagePreview(null);
    setCurrentStoreImage(null);
    setUseCustomImage(false);
    setFormData((prev) => ({
      ...prev,
      image_url: '',
      thumbnail_url: '',
      icon_url: '',
      image_attribution: '',
    }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof StoreFormData, string>> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Store name is required';
    }
    if (!formData.address_line1.trim()) {
      newErrors.address_line1 = 'Address is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    if (selectedImageIndex !== null && storeImages.length === 0 && imagesLoading && !useCustomImage) {
      setApiError('Please wait for images to finish loading before saving.');
      return;
    }

    if (selectedImageIndex !== null && storeImages.length > 0 && !formData.image_url && !useCustomImage) {
      const image = storeImages[selectedImageIndex];
      if (image) {
        setFormData((prev) => ({
          ...prev,
          image_url: image.image_url,
          thumbnail_url: image.thumbnail_url,
          icon_url: image.icon_url,
          image_attribution: image.attribution || '',
        }));
      }
    }

    setLoading(true);
    setApiError(null);

    const payload: Record<string, any> = {
      name: formData.name.trim(),
      address_line1: formData.address_line1.trim(),
      address_line2: formData.address_line2.trim() || null,
      city: formData.city.trim() || null,
      state: formData.state.trim() || null,
      postal_code: formData.postal_code.trim() || null,
      country: formData.country.trim() || null,
      phone_number: formData.phone_number.trim() || null,
      website: formData.website.trim() || null,
      latitude: formData.latitude ? parseFloat(formData.latitude) : null,
      longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      google_place_id: formData.google_place_id || null,
      image_url: formData.image_url || null,
      thumbnail_url: formData.thumbnail_url || null,
      icon_url: formData.icon_url || null,
      image_attribution: formData.image_attribution || null,
    };

    try {
      let savedStore: Store;
      if (isEditing && store) {
        savedStore = await apiFetch(`${apiUrl}/api/shops/${store.id}/`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        savedStore = await apiFetch(`${apiUrl}/api/shops/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (openingHoursPeriods.length > 0 && savedStore.id) {
          for (const period of openingHoursPeriods) {
            try {
              await apiFetch(`${apiUrl}/api/shops/${savedStore.id}/hours/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  day_of_week: period.day,
                  open_time: period.open_time ? `${period.open_time.slice(0, 2)}:${period.open_time.slice(2)}:00` : null,
                  close_time: period.close_time ? `${period.close_time.slice(0, 2)}:${period.close_time.slice(2)}:00` : null,
                  is_closed: false,
                }),
              });
            } catch (err) {
              console.error('Failed to save hours for day', period.day, err);
            }
          }
        }
      }
      onSuccess(savedStore);
      onClose();
    } catch (err: any) {
      setApiError(err.message || 'An error occurred while saving the store.');
    } finally {
      setLoading(false);
    }
  };

  const displayImages = storeImages.length > 0 ? storeImages : thumbnailImages;
  const hasImages = displayImages.length > 0 || imagesLoading;

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        {isEditing ? 'Edit Store' : 'Add New Store'}
      </Typography>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {apiError}
        </Alert>
      )}

      {/* Google Places Search - Only show for new stores */}
      {!isEditing && (
        <Box sx={{ mb: 2 }}>
          {/* Location Section */}
          <Box sx={{ mb: 2 }}>
            {locationLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Detecting your location...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Show location input when needed */}
                {showLocationInput && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                      <TextField
                        size="small"
                        label="Your location"
                        placeholder="e.g., Victoria BC or V8W 1N3"
                        value={useMyLocation ? 'My Location' : manualLocation}
                        onChange={(e) => setManualLocation(e.target.value)}
                        onBlur={handleLocationBlur}
                        disabled={useMyLocation}
                        inputRef={locationInputRef}
                        sx={{ flex: 1 }}
                        InputProps={{
                          readOnly: useMyLocation,
                        }}
                      />
                    </Box>
                    {/* Checkbox - only show if auto-location is available */}
                    {hasAutoLocation && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={useMyLocation}
                            onChange={(e) => handleUseMyLocationChange(e.target.checked)}
                            size="small"
                          />
                        }
                        label="Use my current location"
                      />
                    )}
                  </Box>
                )}

                {/* Change Location button - show when location input is hidden */}
                {!showLocationInput && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                    <Button
                      size="small"
                      startIcon={<EditLocationIcon />}
                      onClick={handleChangeLocationClick}
                    >
                      Change Location
                    </Button>
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Search input */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Autocomplete
              freeSolo
              fullWidth
              open={dropdownOpen && searchResults.length > 0}
              onOpen={() => hasSearched && setDropdownOpen(true)}
              onClose={() => setDropdownOpen(false)}
              options={searchResults}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.name} - ${option.address}`
              }
              inputValue={searchQuery}
              onInputChange={(_, value) => setSearchQuery(value)}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') {
                  handlePlaceSelect(value);
                }
              }}
              loading={searchLoading}
              disabled={!hasValidLocation}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={getSearchPlaceholder()}
                  placeholder={hasValidLocation ? "Enter store name..." : undefined}
                  size="small"
                  onKeyPress={handleSearchKeyPress}
                  disabled={!hasValidLocation}
                  inputRef={searchInputRef}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading && <CircularProgress color="inherit" size={20} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                const alreadyExists = isExistingStore(option.place_id);
                return (
                  <li
                    key={key}
                    {...otherProps}
                    style={{
                      ...otherProps.style,
                      pointerEvents: alreadyExists ? 'none' : 'auto',
                      opacity: alreadyExists ? 0.6 : 1,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        sx={{
                          textDecoration: alreadyExists ? 'line-through' : 'none',
                          color: alreadyExists ? 'text.disabled' : 'text.primary',
                        }}
                      >
                        {option.name}
                        {alreadyExists && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{ ml: 1, fontStyle: 'italic', color: 'text.disabled' }}
                          >
                            (already added)
                          </Typography>
                        )}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: alreadyExists ? 'line-through' : 'none',
                          color: 'text.secondary',
                        }}
                      >
                        {option.address}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
            />
            <Button
              variant={canSearch ? 'contained' : 'outlined'}
              onClick={handleSearch}
              disabled={!canSearch || searchLoading}
              sx={{ minWidth: 56 }}
            >
              <SearchIcon />
            </Button>
          </Box>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Guide text */}
      {!isEditing && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Enter the store details below, or search for a store above and select it to auto-populate.
        </Typography>
      )}

      {/* Store Form */}
      <StoreForm formData={formData} onChange={handleChange} errors={errors} />

      {/* Image Selection Section */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          {isEditing ? 'Store Image' : 'Store Image (optional)'}
        </Typography>

        {/* Show Google images only for new stores */}
        {!isEditing && (
          <>
            {imagesLoading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {imagesProgress.phase}
                </Typography>
                <LinearProgress />
              </Box>
            )}

            {hasImages && !useCustomImage && (
              <ImageList cols={5} rowHeight={100} gap={8} sx={{ mb: 2 }}>
                {displayImages.map((image, index) => (
                  <ImageListItem
                    key={index}
                    onClick={() => !('loading' in image && image.loading) && handleImageSelect(index)}
                    sx={{
                      cursor: ('loading' in image && image.loading) ? 'wait' : 'pointer',
                      border: selectedImageIndex === index ? '3px solid' : '1px solid',
                      borderColor: selectedImageIndex === index ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    {('loading' in image && image.loading) ? (
                      <Skeleton variant="rectangular" width="100%" height="100%" />
                    ) : (
                      <img
                        src={'thumbnail_url' in image ? image.thumbnail_url : ''}
                        alt={`Store ${index + 1}`}
                        loading="lazy"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    )}
                    {selectedImageIndex === index && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          bgcolor: 'primary.main',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                    )}
                  </ImageListItem>
                ))}
              </ImageList>
            )}
          </>
        )}

        {/* Current/Custom image preview */}
        {(customImagePreview || currentStoreImage) && useCustomImage && (
          <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
            <img
              src={customImagePreview || currentStoreImage || ''}
              alt="Store preview"
              style={{
                maxWidth: '200px',
                maxHeight: '150px',
                borderRadius: 8,
                border: '3px solid',
                borderColor: '#1976d2'
              }}
            />
            <IconButton
              size="small"
              onClick={handleClearCustomImage}
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' },
                width: 24,
                height: 24,
                fontSize: 16,
              }}
            >
              ×
            </IconButton>
            <Box
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                bgcolor: 'primary.main',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
              }}
            >
              <CheckCircleIcon sx={{ color: 'white', fontSize: 20 }} />
            </Box>
          </Box>
        )}

        {/* Upload buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />

          <Button
            variant="outlined"
            size="small"
            startIcon={<FileUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingCustom}
          >
            {isEditing ? 'Change Photo' : 'Upload Photo'}
          </Button>

          <Button
            variant="outlined"
            size="small"
            startIcon={<AddAPhotoIcon />}
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploadingCustom}
          >
            Take Photo
          </Button>

          {uploadingCustom && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Uploading...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Attribution */}
        {selectedImageIndex !== null && displayImages[selectedImageIndex] && !useCustomImage && (
          <>
            {(() => {
              const img = displayImages[selectedImageIndex];
              const attribution = 'attribution' in img ? img.attribution : null;
              return attribution ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Photo: {attribution.replace(/<[^>]*>/g, '')}
                </Typography>
              ) : null;
            })()}
          </>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" color="primary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading || uploadingCustom}>
          {loading ? <CircularProgress size={24} /> : isEditing ? 'Save Changes' : 'Add Store'}
        </Button>
      </Box>
    </ModalWrapper>
  );
};

export default StoreFormModal;
